<!-- static/src/components/layout/NavBar.vue -->

<template>
  <header
    class="sticky top-0 z-50 transition-colors duration-200"
    :class="headerClasses"
    :style="headerStyle"
  >
    <div class="container-wide flex items-center h-16 gap-4">
      <!-- Brand (white-label aware) -->
      <!-- Logo box is hard-capped (h-9 + max-w) rather than the old
           `h-[64px] w-auto` -- that let an uploaded logo's own aspect
           ratio decide its rendered WIDTH with zero ceiling, filling the
           entire 64px header height edge-to-edge and, for anything wider
           than roughly square, eating enough of the row's horizontal
           budget to force "Find Leads" and the audits/leads badges onto
           two lines (owner-reported, reproduced whenever branding was
           active). object-contain + a fixed box means any uploaded logo
           -- square, wide, tall -- letterboxes to fit within 140x36px and
           can never blow the row out again, on desktop OR mobile (this
           brand block isn't breakpoint-gated, it's the same element on
           both). Company name gets the same treatment: truncate + a
           responsive max-width, so a long agency name can't reopen the
           identical problem from the other side. -->
      <router-link to="/" class="flex items-center gap-2 shrink-0 min-w-0" @click="closeMobile">
        <template v-if="wlBranding.active && wlBranding.logoUrl">
          <img :src="wlBranding.logoUrl" :alt="wlBranding.name" class="h-9 max-w-[110px] sm:max-w-[140px] object-contain rounded shrink-0" />
        </template>
        <template v-else>
          <!-- favicon-32x32.png, not leadranks-logo.png (PageSpeed 2026-09-09:
               "Est savings of 70.2 KiB" -- leadranks-logo.png is a 512x512,
               72KB source image kept full-size for its OTHER job as the
               og:image social-preview asset across every page; this icon
               only ever renders at 32x32 (w-8 h-8), and favicon-32x32.png
               is the exact same logomark already built at that size, 1.2KB. -->
          <!-- alt="" (2026-09-09, PageSpeed a11y finding): the adjacent
               "LeadRanks" wordmark right after this icon already names the
               brand -- a non-empty alt here made a screen reader announce
               "LeadRanks" twice back to back. Purely decorative next to
               real text, not the sole conveyor of the brand name (compare
               SharedReportPage.vue's own use of this same icon, which has
               no adjacent text and correctly keeps a real alt value). -->
          <img src="/favicon-32x32.png" alt="" class="w-8 h-8 rounded-lg object-cover shadow-soft shrink-0" />
        </template>
        <!-- Hidden below `sm:` ONLY when a real logo image is doing the
             branding job on its own -- at the narrowest widths (down to
             320px) there simply isn't room for a hard-capped logo PLUS a
             readable company name PLUS the mobile menu toggle without
             something giving (measured live: still ~22px short at 320px
             with even a heavily-truncated name). The unbranded "LeadRanks"
             wordmark IS the brand mark with no logo image to carry it, so
             it stays visible at every width -- only the branded+logo case
             hides the redundant text name on the smallest screens. -->
        <span class="font-semibold tracking-tight text-base truncate max-w-[180px] xl:max-w-[150px] 2xl:max-w-[260px]"
          :class="[!wlBranding.active && 'text-ink-900', (wlBranding.active && wlBranding.logoUrl) ? 'hidden sm:inline-block' : '']"
          :style="wlBranding.active ? {color: whiteLabelStore.config?.header_text_color || '#FFFFFF'} : {}">
          <template v-if="wlBranding.name">{{ wlBranding.name }}</template>
          <template v-else>Lead<span class="text-brand-600">Ranks</span></template>
        </span>
      </router-link>

      <!-- Desktop nav -->
      <!-- Breakpoint raised from lg: (1024px) to xl: (1280px) 2026-08-30 --
           real overflow found even in the DEFAULT (unbranded) header at
           1024px: 6 nav items + the Features mega-menu trigger + 2 credit
           badges + New analysis + the user avatar/name is genuinely too
           much content for that width, branding or not (measured live:
           nav links visually collided with the credit badges at exactly
           1024px with plain "LeadRanks" branding, no logo). 1024px is
           also a real, common device width (iPad landscape), not just an
           edge case -- it now correctly gets the mobile hamburger header
           instead of a cramped desktop one. -->
      <nav class="hidden xl:flex items-center gap-0.5 ml-3 min-w-0 overflow-hidden" aria-label="Main navigation">
        <!-- Features: mega-menu (Use cases + By role). @keydown.esc on this
             wrapper catches Escape from any focused descendant (keydown
             bubbles), regardless of which specific link currently has
             focus -- confirmed live this wasn't closing on Escape before. -->
        <div class="relative" v-click-outside="() => featuresMenuOpen = false" @keydown.esc="featuresMenuOpen = false">
          <button
            @click="featuresMenuOpen = !featuresMenuOpen"
            class="flex items-center gap-1 px-2.5 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            :class="[
              wlBranding.active ? 'hover:bg-white/10' : 'text-ink-600 hover:text-ink-900 hover:bg-ink-50',
              !wlBranding.active && (featuresMenuOpen || route.path === '/features') ? '!text-ink-900 !bg-ink-50' : ''
            ]"
            :style="wlBranding.active ? {color: whiteLabelStore.config?.header_text_color || '#FFFFFF'} : {}"
          >
            Features
            <Icon name="chevron-down" :size="14" :classes="featuresMenuOpen ? 'rotate-180 transition-transform duration-150' : 'transition-transform duration-150'" />
          </button>
          <!-- Backdrop: dims the rest of the page while the mega-menu is
               open, and doubles as a click-to-close target. -->
          <Transition name="fade-backdrop">
            <div v-if="featuresMenuOpen" class="fixed inset-0 top-16 bg-ink-900/20 z-40" @click="featuresMenuOpen = false"></div>
          </Transition>

          <!-- Full-width, edge-to-edge under the header (not a capped
               centered box) -- per explicit direction, reversing the v2
               spec's "centered ~1100px panel" note above. -->
          <Transition name="pop">
            <div v-if="featuresMenuOpen" class="fixed left-0 right-0 top-16 bg-white border-b border-ink-100 shadow-float z-50 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div class="container-wide py-8 lg:py-10">
                <div class="eyebrow mb-3"><Icon name="layers" :size="12" /> Explore</div>
                <h2 class="font-display text-2xl lg:text-3xl text-ink-900 mb-8 text-balance">Browse by what you need, or who you are.</h2>

                <!-- Flat flex row -- all 5 items (3 use-case columns, By
                     role, promo) top-aligned in the SAME row at >=1024px
                     (this desktop nav doesn't render below that width
                     anyway). Previously tried a 2-row CSS grid-template-areas
                     scheme to fit "By role" and "promo" in one narrow 5th
                     column; that had a real bug -- "promo"'s row started
                     only after "By role"'s own content height, landing it
                     ~250px below the columns instead of aligned with them
                     (confirmed live via measurement). Flex with
                     items-start avoids that class of bug entirely. -->
                <div class="flex flex-col lg:flex-row gap-6 items-start">
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 flex-1 min-w-0">
                    <div v-for="grp in featuresDropdown.useCaseGroups" :key="grp.label" class="min-w-0">
                      <div class="text-3xs font-bold text-ink-400 uppercase tracking-widest mb-4 px-1">{{ grp.label }}</div>
                      <div v-for="uc in grp.items" :key="uc.label" class="relative"
                        @mouseenter="uc.flagship && (flagshipHovered = true)"
                        @mouseleave="uc.flagship && (flagshipHovered = false)">
                        <router-link :to="uc.to" @click="featuresMenuOpen = false"
                          class="group/row flex items-center gap-2.5 px-1 py-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-brand-600 focus-visible:outline-offset-2 transition-colors">
                          <span class="w-7 h-7 rounded-lg bg-brand-50 group-hover/row:bg-brand-100 flex items-center justify-center shrink-0 transition-colors">
                            <Icon :name="uc.icon" :size="15" classes="text-ink-700 group-hover/row:text-brand-700 transition-colors" />
                          </span>
                          {{ uc.label }}
                        </router-link>

                        <!-- Flagship hover-preview: a simplified, honest
                             illustrative mockup (no real product screenshot
                             asset exists) -- built with plain CSS/divs, not
                             a fake photo or a gray placeholder box. -->
                        <Transition name="preview-slide">
                          <div v-if="uc.flagship && flagshipHovered"
                            class="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-[220px] rounded-lg border border-ink-100 shadow-float bg-white p-3 z-10 pointer-events-none hidden 2xl:block">
                            <div class="flex items-center gap-1.5 text-3xs font-bold text-ink-500 mb-2">
                              <Icon name="robot" :size="12" classes="text-brand-600" /> ChatGPT
                            </div>
                            <div class="rounded-md bg-ink-50 p-2.5 text-3xs text-ink-600 leading-relaxed">
                              "Best SEO audit tool for agencies: <span class="text-brand-700 font-medium">leadranks.io</span>"
                            </div>
                            <div class="flex items-center gap-1.5 mt-2 text-3xs font-medium text-accent-600">
                              <Icon name="check-circle" :size="12" /> Cited by AI
                            </div>
                          </div>
                        </Transition>
                      </div>
                    </div>
                  </div>

                  <div class="w-px self-stretch bg-ink-100 hidden lg:block shrink-0"></div>

                  <!-- By role -->
                  <div class="w-full lg:w-[185px] shrink-0">
                    <div class="text-3xs font-bold text-ink-400 uppercase tracking-widest mb-4 px-1">By role</div>
                    <div class="grid grid-cols-2 lg:grid-cols-1 gap-x-4">
                      <router-link v-for="r in featuresDropdown.byRole" :key="r.label" :to="r.to" @click="featuresMenuOpen = false"
                        class="group/row flex items-center gap-2.5 px-1 py-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-brand-600 focus-visible:outline-offset-2 transition-colors">
                        <span class="w-7 h-7 rounded-lg bg-brand-50 group-hover/row:bg-brand-100 flex items-center justify-center shrink-0 transition-colors">
                          <Icon :name="r.icon" :size="15" classes="text-ink-700 group-hover/row:text-brand-700 transition-colors" />
                        </span>
                        {{ r.label }}
                      </router-link>
                    </div>
                    <router-link to="/features" @click="featuresMenuOpen = false"
                      class="mt-2 flex items-center gap-1.5 px-1 py-2 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors">
                      See all features <Icon name="arrow-right" :size="14" />
                    </router-link>
                  </div>

                  <!-- Promo panel: reuses GeoOptimizationPage's own real
                       headline and links to the actual page. Copy fixed per
                       design review ("ChatGPT can still have never heard of
                       you" read as grammatically muddled) -- also updated on
                       the GeoOptimizationPage.vue hero itself so the two
                       don't diverge, since this panel is a direct quote of
                       it. No people/avatars (deliberate call, see git log)
                       -- the score-ring ties to the "90/100" headline
                       instead, and now animates in on open rather than
                       being static. Real button CTA, not a text link. -->
                  <router-link to="/geo-optimization" @click="featuresMenuOpen = false"
                    class="relative overflow-hidden rounded-xl bg-gradient-to-br from-brand-50 via-brand-50 to-accent-50 border border-brand-100 p-6 w-full lg:w-[210px] shrink-0 flex flex-col items-center text-center hover:border-brand-300 hover:shadow-hero transition-all duration-200 group">
                    <div class="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-accent-300/30 blur-2xl" aria-hidden="true"></div>
                    <div class="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-brand-300/20 blur-2xl" aria-hidden="true"></div>

                    <div class="relative inline-flex items-center gap-1.5 text-3xs font-bold uppercase tracking-widest text-brand-700 bg-white/70 px-2 py-0.5 rounded-full mb-5">
                      <span class="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse"></span>
                      GEO
                    </div>

                    <svg viewBox="0 0 36 36" class="relative w-20 h-20 mb-5">
                      <path class="text-ink-200" stroke="currentColor" stroke-width="3.5" fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                      <path class="text-brand-600 transition-[stroke-dashoffset] duration-[900ms] ease-out" stroke="currentColor" stroke-width="3.5" fill="none" stroke-linecap="round"
                        stroke-dasharray="100"
                        :stroke-dashoffset="ringAnimated ? 10 : 100"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                    </svg>
                    <div class="relative text-base font-bold text-ink-900 leading-snug">Your SEO score can be 90/100.</div>
                    <div class="relative text-xs text-ink-600 mt-1.5 leading-relaxed">AI answers like ChatGPT may have never heard of you.</div>
                    <div class="relative mt-3 flex items-center gap-1 text-3xs text-ink-500">
                      <Icon name="robot" :size="11" classes="text-ink-400" /> Checked against 22 AI crawler bots
                    </div>
                    <span class="relative mt-4 w-full btn-primary btn-sm group-hover:!bg-brand-700">
                      Check my GEO score
                    </span>
                  </router-link>
                </div>
              </div>
            </div>
          </Transition>
        </div>

        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="px-2.5 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
          :class="wlBranding.active ? 'hover:bg-white/10' : 'text-ink-600 hover:text-ink-900 hover:bg-ink-50'"
          :style="wlBranding.active ? {color: whiteLabelStore.config?.header_text_color || '#FFFFFF'} : {}"
          active-class="!text-ink-900 !bg-ink-50"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <div class="flex-1"></div>

      <!-- Right actions -->
      <div class="hidden xl:flex items-center gap-2 shrink-0">
        <template v-if="userStore.loading">
          <div class="skeleton w-24 h-9 rounded-lg"></div>
        </template>

        <template v-else-if="userStore.isAuthenticated">
          <Tooltip
            :title="`${userStore.reportsRemaining} reports remaining`"
            :body="userStore.reportsRemaining <= 3
              ? 'You are running low. Upgrade to keep analyzing without interruption.'
              : 'Each analysis uses one report credit. Credits refresh with your plan.'"
            icon="bi bi-file-earmark-text"
            link-text="View plans"
            link-href="/pricing"
            placement="bottom"
          >
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap shrink-0"
              :class="reportsBadgeClass">
              <span class="w-1.5 h-1.5 rounded-full" :class="reportsDotClass"></span>
              {{ userStore.reportsRemaining }} audits
            </span>
          </Tooltip>

          <Tooltip
            v-if="showLeadCreditsBadge"
            :title="`${userStore.leadCreditsRemaining} lead credits remaining`"
            :body="userStore.leadCreditsRemaining <= 3
              ? 'Running low — buy a credit pack or upgrade to keep prospecting without interruption.'
              : 'Each lead search uses one credit. Credits refresh with your plan, or top up anytime.'"
            icon="bi bi-people"
            link-text="View plans"
            link-href="/pricing"
            placement="bottom"
          >
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border whitespace-nowrap shrink-0"
              :class="leadCreditsBadgeClass">
              <span class="w-1.5 h-1.5 rounded-full" :class="leadCreditsDotClass"></span>
              {{ userStore.leadCreditsRemaining }} leads
            </span>
          </Tooltip>

          <router-link to="/analyze" class="btn-sm whitespace-nowrap shrink-0" :class="wlBranding.active ? 'btn-primary bg-white/90 !text-ink-900 hover:bg-white' : 'btn-outline'">
            <Icon name="sparkles" :size="14" /> New analysis
          </router-link>

          <!-- User dropdown -->
          <div class="relative" v-click-outside="() => userMenuOpen = false">
            <button
              class="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg transition-colors"
              :class="wlBranding.active ? 'hover:bg-white/10' : 'hover:bg-ink-50'"
              @click="userMenuOpen = !userMenuOpen"
            >
              <span class="w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center"
                :class="wlBranding.active ? 'bg-white/90 text-ink-900' : 'bg-gradient-to-br from-brand-600 to-brand-800 text-white'">
                {{ initials }}
              </span>
              <span class="text-sm font-medium" :class="!wlBranding.active && 'text-ink-800'" :style="wlBranding.active ? {color: whiteLabelStore.config?.header_text_color || '#FFFFFF'} : {}">{{ userStore.userName }}</span>
              <Icon name="chevron-down" :size="14" :classes="wlBranding.active ? '' : 'text-ink-500'"
                :style="wlBranding.active ? {color: whiteLabelStore.config?.header_text_color || '#FFFFFF'} : {}" />
            </button>
            <Transition name="pop">
              <div v-if="userMenuOpen"
                class="absolute right-0 top-full mt-2 w-64 card shadow-float py-2 overflow-hidden">
                <div class="px-4 py-2 border-b border-ink-100 mb-1">
                  <div class="text-sm font-medium text-ink-900 truncate">{{ userStore.userFullName || userStore.userName }}</div>
                  <div class="text-xs text-ink-500 truncate">{{ userStore.user?.email }}</div>
                </div>
                <router-link v-for="m in userMenu" :key="m.to" :to="m.to"
                  class="flex items-center gap-3 px-4 py-2 text-sm text-ink-700 hover:bg-ink-50 hover:text-ink-900 transition-colors">
                  <Icon :name="m.icon" :size="16" classes="text-ink-500" />
                  {{ m.label }}
                </router-link>
                <div class="my-1 h-px bg-ink-100"></div>
                <button class="w-full flex items-center gap-3 px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
                  @click="handleLogout" :disabled="loggingOut">
                  <Icon name="logout" :size="16" />
                  {{ loggingOut ? 'Signing out…' : 'Sign out' }}
                </button>
              </div>
            </Transition>
          </div>
        </template>

        <template v-else>
          <router-link to="/login" class="btn-ghost btn-sm">Sign in</router-link>
          <router-link to="/register" class="btn-primary btn-sm">
            Start free
            <Icon name="arrow-right" :size="14" />
          </router-link>
        </template>
      </div>

      <!-- Mobile toggle -->
      <button class="xl:hidden btn-ghost !p-2" @click="mobileOpen = !mobileOpen" aria-label="Menu">
        <Icon :name="mobileOpen ? 'x' : 'menu'" :size="20" />
      </button>
    </div>

    <!-- Mobile drawer -->
    <Transition name="drawer">
      <div v-if="mobileOpen" class="xl:hidden border-t border-ink-100 bg-white">
        <div class="container-wide py-3 space-y-1">
          <!-- Features: expandable (Use cases + By role) -->
          <div>
            <button
              @click="mobileFeaturesOpen = !mobileFeaturesOpen"
              class="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-ink-700 rounded-lg hover:bg-ink-50"
              :class="route.path === '/features' ? '!text-ink-900 !bg-ink-50' : ''"
            >
              Features
              <Icon name="chevron-down" :size="16" :classes="mobileFeaturesOpen ? 'rotate-180 transition-transform duration-150' : 'transition-transform duration-150'" />
            </button>
            <div v-show="mobileFeaturesOpen" class="pl-3 mt-1 space-y-3 pb-2">
              <div v-for="grp in featuresDropdown.useCaseGroups" :key="grp.label">
                <div class="text-3xs font-bold text-ink-400 uppercase tracking-widest mb-1.5 px-3">{{ grp.label }}</div>
                <router-link v-for="uc in grp.items" :key="uc.label" :to="uc.to" @click="closeMobile"
                  class="flex items-center gap-2.5 px-3 py-2 text-sm text-ink-600 rounded-lg hover:bg-ink-50 hover:text-ink-900">
                  <span class="w-6 h-6 rounded-md bg-brand-50 flex items-center justify-center shrink-0">
                    <Icon :name="uc.icon" :size="13" classes="text-ink-700" />
                  </span>
                  {{ uc.label }}
                </router-link>
              </div>
              <div>
                <div class="text-3xs font-bold text-ink-400 uppercase tracking-widest mb-1.5 px-3">By role</div>
                <router-link v-for="r in featuresDropdown.byRole" :key="r.label" :to="r.to" @click="closeMobile"
                  class="flex items-center gap-2.5 px-3 py-2 text-sm text-ink-600 rounded-lg hover:bg-ink-50 hover:text-ink-900">
                  <span class="w-6 h-6 rounded-md bg-brand-50 flex items-center justify-center shrink-0">
                    <Icon :name="r.icon" :size="13" classes="text-ink-700" />
                  </span>
                  {{ r.label }}
                </router-link>
                <router-link to="/features" @click="closeMobile"
                  class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-700">
                  See all features <Icon name="arrow-right" :size="14" />
                </router-link>
              </div>
            </div>
          </div>

          <router-link v-for="item in navItems" :key="item.to" :to="item.to"
            class="block px-3 py-2.5 text-sm font-medium text-ink-700 rounded-lg hover:bg-ink-50"
            active-class="!text-ink-900 !bg-ink-50">
            {{ item.label }}
          </router-link>

          <div class="pt-3 mt-2 border-t border-ink-100">
            <template v-if="userStore.isAuthenticated">
              <router-link v-for="m in userMenu" :key="m.to" :to="m.to"
                class="flex items-center gap-3 px-3 py-2.5 text-sm text-ink-700 rounded-lg hover:bg-ink-50">
                <Icon :name="m.icon" :size="16" classes="text-ink-500" />
                {{ m.label }}
              </router-link>
              <button class="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-danger-600 rounded-lg hover:bg-danger-50"
                @click="handleLogout" :disabled="loggingOut">
                <Icon name="logout" :size="16" /> {{ loggingOut ? 'Signing out…' : 'Sign out' }}
              </button>
            </template>
            <template v-else>
              <div class="flex flex-col gap-2 pt-1">
                <router-link to="/login" class="btn-outline w-full">Sign in</router-link>
                <router-link to="/register" class="btn-primary w-full">
                  Start free <Icon name="arrow-right" :size="14" />
                </router-link>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '../../stores/userStore';
import { useWhiteLabelStore } from '../../stores/whiteLabelStore';
import Icon from '../ui/Icon.vue';
import Tooltip from '../ui/Tooltip.vue';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const whiteLabelStore = useWhiteLabelStore();

const wlBranding = computed(() => ({
  active: whiteLabelStore.isActive,
  name: whiteLabelStore.companyName,
  logoUrl: whiteLabelStore.logoUrl,
  primary: whiteLabelStore.colors.primary,
  secondary: whiteLabelStore.colors.secondary,
}));

// Real bug fixed 2026-09-09 (owner: page scrolling gittery on every page).
// backdrop-blur-md used to sit as a STATIC class on <header>, active at all
// times regardless of scroll position. position:sticky + backdrop-filter is
// a well-documented cause of scroll jank -- the browser has to recompute the
// blur against whatever content is passing underneath on every frame, for
// as long as the user is scrolling, and this header is present on every
// single page. The blur is only ever visually meaningful for the moment
// the header sits semi-transparent over the hero, before any scrolling has
// happened -- once scrolled, header content should read against a fully
// opaque background instead, where blurring what's behind it is invisible
// AND wasted work. White-label mode was already fully opaque (a solid
// gradient) at every scroll position, so the blur there was pure waste
// with zero visual benefit, in every state -- dropped entirely.
const headerClasses = computed(() => {
  if (wlBranding.value.active) {
    return scrolled.value ? 'border-b border-white/20 shadow-soft' : 'border-b border-transparent';
  }
  return scrolled.value
    ? 'border-b border-ink-100 shadow-soft'
    : 'border-b border-transparent backdrop-blur-md';
});

const headerStyle = computed(() => {
  if (wlBranding.value.active) {
    var tc = whiteLabelStore.config?.header_text_color || '#FFFFFF';
    return { background: 'linear-gradient(135deg, ' + wlBranding.value.primary + ', ' + wlBranding.value.secondary + ')', color: tc };
  }
  return { background: scrolled.value ? '#fff' : 'rgba(255,255,255,0.9)' };
});

// ============================================================================
// STATE
// ============================================================================

const mobileOpen = ref(false);
const userMenuOpen = ref(false);
const featuresMenuOpen = ref(false);
const mobileFeaturesOpen = ref(false);
const flagshipHovered = ref(false);
// GEO ring animates its fill in once per open (not on every hover) --
// starts empty (dashoffset 100), flips to the real value shortly after the
// panel mounts so the CSS transition actually plays instead of jumping
// straight to the end state.
const ringAnimated = ref(false);
watch(featuresMenuOpen, (open) => {
  if (open) {
    ringAnimated.value = false;
    requestAnimationFrame(() => requestAnimationFrame(() => { ringAnimated.value = true; }));
  } else {
    ringAnimated.value = false;
    flagshipHovered.value = false;
  }
});
const scrolled = ref(false);
const loggingOut = ref(false);

// ============================================================================
// NAVIGATION ITEMS - UPDATED with Prospecting
// ============================================================================
// 'Features' is handled separately as a mega-menu trigger (see
// featuresDropdown below), not a plain link -- deliberately not in this
// list.

const navItems = [
  { to: '/pricing',      label: 'Pricing' },
  { to: '/prospecting',  label: 'Find Leads' },
  { to: '/affiliates',   label: 'Affiliates' },
  { to: '/campaigns',    label: 'Campaigns' },
  { to: '/learn',        label: 'Learn' },
  { to: '/about',        label: 'About' },
];

// ============================================================================
// FEATURES MEGA-MENU — Use cases + By role
// ============================================================================
// Every destination here is real, already-existing content (FeaturesPage's
// group/feature anchors, PricingPage's tier anchors, or the dedicated
// /geo-optimization page) -- no new pages or copy invented for this menu.
// "By size" and "By industry" (the other two segmentations Semrush uses)
// were deliberately left out: neither has distinct, real content behind it
// yet -- see CLAUDE.md if this is revisited.

const featuresDropdown = {
  // Split into 3 thematic groups rather than one flat list of 8 -- deliberately
  // NOT 4+ groups: with only 8 items total, more groups than this would mean
  // 1-2 items per column, which looks sparse/over-segmented rather than
  // genuinely organized. Grouping reflects real thematic overlap in the
  // content, not an arbitrary split for visual width.
  // Labels shortened per the v2 design spec -- fixes wrapping at the
  // content layer (most were 22-26 chars, now 10-20), not just CSS.
  // "AI recommendations" is flagged as the flagship item (gets the hover
  // preview, see flagshipPreview below).
  useCaseGroups: [
    {
      label: 'Rank & Get Found',
      items: [
        { label: 'Search visibility',   icon: 'search',   to: '/features#deep-seo-analysis' },
        { label: 'AI visibility',       icon: 'sparkles', to: '/geo-optimization', flagship: true },
      ],
    },
    {
      label: 'Improve Your Site',
      items: [
        { label: 'Content quality',    icon: 'file-text', to: '/features#content-quality-scoring' },
        { label: 'Technical fixes',    icon: 'sliders',   to: '/features#performance-speed' },
        { label: 'Link authority',     icon: 'link',      to: '/features#backlink-authority' },
      ],
    },
    {
      label: 'Compete & Grow',
      items: [
        { label: 'Competitor gap',      icon: 'bar-chart', to: '/features#competitor-gap-analysis' },
        { label: 'Client strategy',     icon: 'briefcase', to: '/features#action-workflow' },
        { label: 'Warm leads',          icon: 'target',    to: '/features#find-warm-leads' },
        { label: 'White-label',         icon: 'palette',   to: '/features#white-label' },
      ],
    },
  ],
  byRole: [
    { label: 'Business Owner',        icon: 'user',   to: '/pricing#starter' },
    { label: 'SEO Freelancer',       icon: 'zap',    to: '/pricing#professional' },
    { label: 'Agency Owner',          icon: 'users',  to: '/pricing#agency' },
    { label: 'Enterprise',            icon: 'globe',  to: '/pricing#enterprise' },
  ],
};

// ============================================================================
// USER MENU ITEMS
// ============================================================================

const userMenu = [
  { to: '/dashboard',           label: 'Dashboard',     icon: 'bar-chart' },
  { to: '/analyze',             label: 'New analysis',  icon: 'search' },
  { to: '/prospecting',         label: 'Find Leads',    icon: 'target' },
  { to: '/referrals',           label: 'Referrals',     icon: 'users' },
  { to: '/affiliates/dashboard',label: 'Partner Dashboard', icon: 'dollar-sign' },
  { to: '/history',             label: 'History',       icon: 'history' },
  { to: '/settings',            label: 'Settings',      icon: 'settings' },
  { to: '/settings/white-label',label: 'White-Label Branding', icon: 'palette' },
];

// ============================================================================
// COMPUTED
// ============================================================================

const initials = computed(() => {
  const n = userStore.userFullName || userStore.userName || 'U';
  return n.split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
});

const reportsBadgeClass = computed(() => {
  const n = userStore.reportsRemaining;
  if (n <= 0) return 'bg-danger-50 text-danger-700 border-danger-100';
  if (n <= 3) return 'bg-warn-50 text-warn-700 border-warn-100';
  return 'bg-accent-50 text-accent-700 border-accent-100';
});

const reportsDotClass = computed(() => {
  const n = userStore.reportsRemaining;
  if (n <= 0) return 'bg-danger-500';
  if (n <= 3) return 'bg-warn-500';
  return 'bg-accent-500';
});

// Added 2026-08-29 (owner: header should show lead credits too, not
// just audits) -- same tiered coloring as reportsBadgeClass/
// reportsDotClass above. Gated on leadCreditsLimit > 0. Starter used to be
// a real 0 here (no prospecting at all) and this badge stayed hidden so it
// wouldn't read as "a feature that exists but is exhausted". Since
// 2026-09-18 Starter carries a 3-credit taste, so the badge now shows a
// real "3 of 3" for free accounts too.
const showLeadCreditsBadge = computed(() => userStore.leadCreditsLimit > 0);
const leadCreditsBadgeClass = computed(() => {
  const n = userStore.leadCreditsRemaining;
  if (n <= 0) return 'bg-danger-50 text-danger-700 border-danger-100';
  if (n <= 3) return 'bg-warn-50 text-warn-700 border-warn-100';
  return 'bg-accent-50 text-accent-700 border-accent-100';
});
const leadCreditsDotClass = computed(() => {
  const n = userStore.leadCreditsRemaining;
  if (n <= 0) return 'bg-danger-500';
  if (n <= 3) return 'bg-warn-500';
  return 'bg-accent-500';
});

// ============================================================================
// METHODS
// ============================================================================

const closeMobile = () => {
  mobileOpen.value = false;
  mobileFeaturesOpen.value = false;
};

// Close mobile drawer + any open dropdowns after any navigation completes
watch(() => route.fullPath, () => {
  mobileOpen.value = false;
  mobileFeaturesOpen.value = false;
  userMenuOpen.value = false;
  featuresMenuOpen.value = false;
});

const handleLogout = async () => {
  if (loggingOut.value) return;
  loggingOut.value = true;
  userMenuOpen.value = false;
  closeMobile();

  await userStore.logout();

  // Fallback redirect if store doesn't handle it
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// ============================================================================
// SCROLL HANDLER
// ============================================================================

const onScroll = () => {
  scrolled.value = window.scrollY > 8;
};

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
});
</script>

<script>
// ============================================================================
// CLICK-OUTSIDE DIRECTIVE
// ============================================================================

const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = (e) => {
      if (!el.contains(e.target)) binding.value(e);
    };
    document.addEventListener('click', el._clickOutside);
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside);
  }
};

export default {
  directives: { 'click-outside': vClickOutside },
};
</script>

<style scoped>
/* ============================================================================
   TRANSITIONS
   ============================================================================ */

/* User menu popover */
.pop-enter-active,
.pop-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Mega-menu backdrop */
.fade-backdrop-enter-active,
.fade-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.fade-backdrop-enter-from,
.fade-backdrop-leave-to {
  opacity: 0;
}

/* Flagship hover-preview */
.preview-slide-enter-active,
.preview-slide-leave-active {
  transition: opacity 180ms ease-out, transform 180ms ease-out;
}
.preview-slide-enter-from,
.preview-slide-leave-to {
  opacity: 0;
  transform: translate(4px, -50%);
}
.preview-slide-enter-to,
.preview-slide-leave-from {
  transform: translate(0, -50%);
}

/* Mobile drawer */
.drawer-enter-active,
.drawer-leave-active {
  transition: max-height 280ms cubic-bezier(0.22, 0.61, 0.36, 1),
              opacity 180ms ease;
  overflow: hidden;
}

.drawer-enter-from,
.drawer-leave-to {
  max-height: 0;
  opacity: 0;
}

.drawer-enter-to,
.drawer-leave-from {
  max-height: 600px;
  opacity: 1;
}
</style>